import React, { useRef } from "react";
import { toast } from "sonner";
import { Camera, UploadSimple, X } from "@phosphor-icons/react";

// Compress an image File to a JPEG data URL. Max 512x512 keeps avatars tiny.
async function compressAvatar(file, maxSize = 512, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          const ratio = Math.min(maxSize / width, maxSize / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const c = document.createElement("canvas");
        c.width = width; c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Compact avatar picker used in the customer form and self-service profile.
 * - Preview shows the current photo or a placeholder camera icon.
 * - Two buttons: Camera (opens device camera on mobile via capture) and Gallery.
 * - Remove button when a photo is set.
 * Props:
 *   value: string | null (data URL or external URL)
 *   onChange: (dataUrl|null) => void
 *   size?: 'sm' | 'md' | 'lg'   default md (80px)
 *   testid?: string
 */
export default function CustomerPhotoField({ value, onChange, size = "md", testid = "cust-photo" }) {
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);

  const pick = async (file) => {
    if (!file) return;
    try {
      const dataUrl = await compressAvatar(file);
      onChange(dataUrl);
    } catch {
      toast.error("Falha ao ler a imagem");
    }
  };

  const dim = size === "sm" ? "w-14 h-14" : size === "lg" ? "w-24 h-24" : "w-20 h-20";

  return (
    <div className="flex items-center gap-3">
      <div className={`${dim} border-2 border-brand-ink bg-white overflow-hidden shrink-0 rounded-full`}>
        {value ? (
          <img data-testid={`${testid}-preview`} src={value} alt="Avatar" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-brand-ink2">
            <Camera weight="bold" size={size === "sm" ? 18 : 24} />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="user"
          className="hidden"
          data-testid={`${testid}-camera-input`}
          onChange={(e) => pick(e.target.files?.[0])}
        />
        <input
          ref={galleryRef}
          type="file"
          accept="image/*"
          className="hidden"
          data-testid={`${testid}-gallery-input`}
          onChange={(e) => pick(e.target.files?.[0])}
        />
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => cameraRef.current?.click()}
            data-testid={`${testid}-camera-btn`}
            className="btn-white !h-9 !px-2 text-xs"
            title="Tirar foto com a câmera"
          >
            <Camera weight="bold" size={14} /> Câmera
          </button>
          <button
            type="button"
            onClick={() => galleryRef.current?.click()}
            data-testid={`${testid}-gallery-btn`}
            className="btn-white !h-9 !px-2 text-xs"
            title="Escolher da galeria"
          >
            <UploadSimple weight="bold" size={14} /> Galeria
          </button>
          {value && (
            <button
              type="button"
              onClick={() => onChange(null)}
              data-testid={`${testid}-clear-btn`}
              className="btn-white !h-9 !px-2 text-xs !bg-brand-peach"
              title="Remover foto"
            >
              <X weight="bold" size={14} />
            </button>
          )}
        </div>
        <p className="text-[11px] text-brand-ink2">Aparece no dropdown do Registro Rápido.</p>
      </div>
    </div>
  );
}
